/**
 * 集中式日志记录工具
 * 提供标准化的日志功能，支持不同的环境和日志级别
 */

// 日志级别
export const LOG_LEVELS = {
  DEBUG: 0,   // 调试信息，仅在开发环境显示
  INFO: 1,    // 一般信息
  WARN: 2,    // 警告信息
  ERROR: 3,   // 错误信息
  NONE: 99    // 禁用日志
};

// 当前环境
const isProduction = process.env.NODE_ENV === 'production';
const isTest = process.env.NODE_ENV === 'test';

// 默认日志级别：生产环境只显示警告和错误，其他环境显示所有日志
const defaultLogLevel = isProduction ? LOG_LEVELS.WARN : LOG_LEVELS.DEBUG;

// 当前日志级别，可通过setLogLevel动态修改
let currentLogLevel = defaultLogLevel;

/**
 * 设置日志级别
 * @param {number} level - 日志级别
 */
export function setLogLevel(level) {
  if (Object.values(LOG_LEVELS).includes(level)) {
    currentLogLevel = level;
    debug('日志级别已设置为', getLevelName(level));
  } else {
    warn('无效的日志级别:', level);
  }
}

/**
 * 获取日志级别名称
 * @param {number} level - 日志级别
 * @returns {string} 日志级别名称
 */
function getLevelName(level) {
  return Object.keys(LOG_LEVELS).find(key => LOG_LEVELS[key] === level) || 'UNKNOWN';
}

/**
 * 格式化日志消息
 * @param {string} level - 日志级别名称
 * @param {Array} args - 日志参数
 * @returns {string} 格式化后的日志消息
 */
function formatLogMessage(level, args) {
  const timestamp = new Date().toISOString();
  const message = args.map(arg => 
    typeof arg === 'object' ? JSON.stringify(arg) : String(arg)
  ).join(' ');
  
  return `[${timestamp}] [${level}] ${message}`;
}

/**
 * 输出调试日志
 */
export function debug(...args) {
  if (currentLogLevel <= LOG_LEVELS.DEBUG && !isTest) {
    console.debug(formatLogMessage('DEBUG', args));
  }
}

/**
 * 输出信息日志
 */
export function info(...args) {
  if (currentLogLevel <= LOG_LEVELS.INFO && !isTest) {
    console.info(formatLogMessage('INFO', args));
  }
}

/**
 * 输出警告日志
 */
export function warn(...args) {
  if (currentLogLevel <= LOG_LEVELS.WARN && !isTest) {
    console.warn(formatLogMessage('WARN', args));
  }
}

/**
 * 输出错误日志
 */
export function error(...args) {
  if (currentLogLevel <= LOG_LEVELS.ERROR && !isTest) {
    console.error(formatLogMessage('ERROR', args));
  }
}

/**
 * 分组日志（开始）
 */
export function group(label) {
  if (!isTest && !isProduction) {
    console.group(label);
  }
}

/**
 * 分组日志（结束）
 */
export function groupEnd() {
  if (!isTest && !isProduction) {
    console.groupEnd();
  }
}

/**
 * 测量性能
 * @param {string} label - 性能测量标签
 * @param {Function} fn - 要测量的函数
 * @returns {*} 函数执行结果
 */
export function measure(label, fn) {
  if (currentLogLevel <= LOG_LEVELS.DEBUG && !isTest) {
    console.time(label);
    const result = fn();
    console.timeEnd(label);
    return result;
  } else {
    return fn();
  }
}

export default {
  debug,
  info,
  warn,
  error,
  group,
  groupEnd,
  measure,
  setLogLevel,
  LOG_LEVELS
}; 